import { useEffect, useState } from 'react';
import { supabase } from '~/services/supabase';

export interface ISlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
}

interface IUseSlackChannelsResult {
  channels: ISlackChannel[];
  loading: boolean;
  error: string | null;
}

export const useSlackChannels = (enabled: boolean): IUseSlackChannelsResult => {
  const [channels, setChannels] = useState<ISlackChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setChannels([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchChannels = async (): Promise<void> => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke('slack-oauth', {
          body: { action: 'listChannels' },
        });

        if (cancelled) {
          return;
        }

        if (fnError) {
          throw fnError;
        }

        setChannels((data?.channels as ISlackChannel[] | undefined) ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load Slack channels');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchChannels();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { channels, loading, error };
};
